import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { CustomTableToolbar } from "../../components/CustomTableToolbar";
import {
  ISidekickPlantSummary,
  ISidekickUserSummary,
} from "../../types/api/sidekick-usage.type";
import { USER_KIND_LABELS, formatDateTime } from "./labels";

const dateCol = (field: string, headerName: string): GridColDef => ({
  field,
  headerName,
  flex: 1,
  minWidth: 150,
  valueFormatter: ({ value }) => formatDateTime(value as string),
});

const numberCol = (field: string, headerName: string): GridColDef => ({
  field,
  headerName,
  type: "number",
  flex: 0.6,
  minWidth: 110,
});

/** DataGrid with the shared quick-search toolbar, filtering rows client-side. */
const SearchableGrid = <T,>({
  rows,
  columns,
  getRowId,
  searchFields,
  onRowClick,
}: {
  rows: T[];
  columns: GridColDef[];
  getRowId: (row: T) => string | number;
  searchFields: (row: T) => string[];
  onRowClick?: (row: T) => void;
}) => {
  const [searchText, setSearchText] = useState("");
  const [, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      searchFields(row).some((v) => v.toLowerCase().includes(needle))
    );
  }, [rows, searchText, searchFields]);

  return (
    <DataGrid
      autoHeight
      density="compact"
      rows={filteredRows}
      columns={columns}
      getRowId={getRowId as any}
      pageSize={10}
      rowsPerPageOptions={[10]}
      disableSelectionOnClick
      onRowClick={
        onRowClick ? (params: GridRowParams) => onRowClick(params.row as T) : undefined
      }
      sx={onRowClick ? { "& .MuiDataGrid-row": { cursor: "pointer" } } : undefined}
      components={{ Toolbar: CustomTableToolbar }}
      componentsProps={{
        toolbar: {
          setFilterButtonEl,
          searchValue: searchText,
          onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            setSearchText(event.target.value),
          clearSearch: () => setSearchText(""),
          showDeleted: false,
          onShowDeletedChange: () => {},
          showDeletedCheckbox: false,
        },
      }}
    />
  );
};

const plantColumns: GridColDef[] = [
  { field: "name", headerName: "Plant", flex: 1.5, minWidth: 180 },
  numberCol("turns", "Questions"),
  numberCol("sessions", "Conversations"),
  numberCol("users", "People"),
  dateCol("first", "First question"),
  dateCol("last", "Latest question"),
];

const plantSearch = (row: ISidekickPlantSummary) => [row.name, String(row.env_id)];

export const PlantTable = ({ rows }: { rows: ISidekickPlantSummary[] }) => (
  <SearchableGrid
    rows={rows}
    columns={plantColumns}
    getRowId={(row) => row.env_id}
    searchFields={plantSearch}
  />
);

export const UserTable = ({
  rows,
  plantNames,
  onSelectUser,
}: {
  rows: ISidekickUserSummary[];
  plantNames: Record<number, string>;
  onSelectUser: (email: string) => void;
}) => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: "email", headerName: "Person", flex: 1.5, minWidth: 200 },
      {
        field: "kind",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        valueFormatter: ({ value }) =>
          USER_KIND_LABELS[value as ISidekickUserSummary["kind"]] ?? String(value),
      },
      numberCol("turns", "Questions"),
      numberCol("sessions", "Conversations"),
      {
        field: "env_ids",
        headerName: "Plants",
        flex: 1.5,
        minWidth: 160,
        sortable: false,
        valueGetter: ({ row }) =>
          (row.env_ids as number[]).map((id) => plantNames[id] ?? `#${id}`).join(", "),
      },
      dateCol("first", "First question"),
      dateCol("last", "Latest question"),
    ],
    [plantNames]
  );
  const search = useMemo(
    () => (row: ISidekickUserSummary) => [
      row.email,
      USER_KIND_LABELS[row.kind] ?? row.kind,
      ...row.env_ids.map((id) => plantNames[id] ?? ""),
    ],
    [plantNames]
  );
  return (
    <Box data-testid="by-person-table">
      <Typography variant="body2" color="#999" sx={{ mb: 1 }}>
        Click a person to see only their conversations.
      </Typography>
      <SearchableGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.email}
        searchFields={search}
        onRowClick={(row) => onSelectUser(row.email)}
      />
    </Box>
  );
};
